/**
 * Elementos del brand kit que **sí funcionan sobre datos**.
 *
 * El kit tiene dos familias y no se comportan igual. El **trazo** —brochazos, garabatos,
 * círculos a mano, marcos, rayas— vive en [`pincel.tsx`](pincel.tsx) y es exclusivo de la
 * vitrina: sobre una tabla densa compite con el dato y lo vuelve ilegible (FEATURE-100
 * RF-9). Lo que está acá es la otra familia: **trama, micro-tipografía y chips**, que el
 * kit usa dentro de la propia consola —el semitono al 6 % del sidebar y al 10 % del campo
 * de puntos— y que por lo tanto no cae bajo esa prohibición.
 *
 * La separación es literal para que no haya que acordarse de la regla: la consola importa
 * de acá y **no puede** importar un brochazo sin que se note en el import.
 *
 * Los tres tokens de color (`Tono`, `COLOR`, `TEXTO`) viven acá porque los usan las dos
 * familias; `pincel.tsx` los consume desde este módulo.
 */
import type { ReactNode } from "react";

/* ══ Color ═════════════════════════════════════════════════════════════════
 * Los colores del kit, siempre como token. `loss` solo donde significa alerta o
 * descenso — nunca como decoración (paleta de la hoja 2 del kit).
 * ═══════════════════════════════════════════════════════════════════════════ */

export type Tono = "accent" | "accent2" | "win" | "loss" | "gold" | "ink" | "muted";

export const COLOR: Record<Tono, string> = {
  accent: "var(--ld-accent)",
  accent2: "var(--ld-accent-2)",
  win: "var(--ld-win)",
  loss: "var(--ld-loss)",
  gold: "var(--ld-gold)",
  ink: "var(--ld-ink)",
  muted: "var(--ld-muted)",
};

export const TEXTO: Record<Tono, string> = {
  accent: "text-accent",
  accent2: "text-accent-2",
  win: "text-win",
  loss: "text-loss",
  gold: "text-gold",
  ink: "text-ink",
  muted: "text-muted",
};

/* ══ Trama (kit 19) ════════════════════════════════════════════════════════ */

/**
 * Puntos que se apagan hacia un lado. Es la capa de textura del chrome de la consola:
 * al 6 % en el sidebar y al 10 % en el campo de puntos de la esquina superior derecha.
 */
export function Semitono({
  tono,
  className = "",
  desde = "left",
  paso = 13,
  radio = 2,
  hasta = "78%",
}: {
  tono: Tono;
  className?: string;
  /**
   * Borde por el que el patrón entra a full; se desvanece hacia el opuesto.
   * `"vertical"` lo apaga por **ambos** extremos: para una columna larga (un
   * sidebar), entrar a full por arriba y cortarse en seco abajo se ve como una
   * mancha con borde.
   */
  desde?: "left" | "right" | "top" | "bottom" | "vertical";
  paso?: number;
  /** Radio del punto, en px. */
  radio?: number;
  /** Punto del degradado donde el patrón ya desapareció. */
  hasta?: string;
}) {
  const opuesto = { left: "right", right: "left", top: "bottom", bottom: "top" };
  const mask =
    desde === "vertical"
      ? "linear-gradient(to bottom, transparent, #000 16%, #000 70%, transparent)"
      : `linear-gradient(to ${opuesto[desde]}, #000, transparent ${hasta})`;
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none ${className}`}
      style={{
        backgroundImage: `radial-gradient(${COLOR[tono]} ${radio}px, transparent ${radio + 0.1}px)`,
        backgroundSize: `${paso}px ${paso}px`,
        maskImage: mask,
        WebkitMaskImage: mask,
      }}
    />
  );
}

/* ══ Tipografía y chips de la consola ══════════════════════════════════════ */

/** Micro-label: caja alta, tracking abierto. Rol «label» del ADR-004 §3.4. */
export function Micro({
  children,
  tono = "muted",
  className = "",
}: {
  children: ReactNode;
  tono?: Tono;
  className?: string;
}) {
  return <span className={`font-display text-xs uppercase tracking-[0.18em] ${TEXTO[tono]} ${className}`}>{children}</span>;
}

/** Chip de «en vivo»: el punto late para que se lea sin leer. */
export function ChipVivo({ children = "En vivo" }: { children?: ReactNode }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-accent-2/40 bg-accent-2/10 px-2.5 py-0.5 text-xs font-bold text-accent-2">
      <span aria-hidden="true" className="ld-live-pulse inline-block h-1.5 w-1.5 rounded-full bg-accent-2" />
      {children}
    </span>
  );
}
